import {
  Button,
  Col,
  Flex,
  LegacyForm,
  Modal,
  Row,
  Typography,
  Upload,
  useMessagedAsync,
  useMessagedAsyncFn,
  usePaypingContext,
} from "@payping/web-components";
import { FailedDollar, UploadOutlined } from "@payping/icons";
import { deleteErrorsId, fileUploadId } from "@/assets/googleTrackIds";
import ManualRequestSaman from "@/pages/DigitalAccount/Saman/Create/ManualRequestSaman";
import type { FormInstance } from "@payping/web-components/legacyComponents/Form/types";
import React, { useState } from "react";
import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import TransferList from "@/pages/DigitalAccount/Saman/Create/SecondStep/TransferList";
import type { UploadChangeParam } from "@payping/web-components/components/upload/types";
import { BatchPaymentDetailsReasonCodeEnum, TransferMethodEnum } from "@payping/api";
import { setSamanUpdateRequestList, setSamanUploadList } from "@payping/redux/actions";
import { SamanConfiguration } from "@/pages/DigitalAccount/Saman/Create";

export interface SamanRowType {
  id?: string;
  iban: string;
  amount: string;
  description?: string;
  error?: string;
  tableIdCol: number;
  fullName?: string;
  transferMethod?: TransferMethodEnum;
  reasonCode?: BatchPaymentDetailsReasonCodeEnum;
  transfer?: SamanRowType[];
}

interface Props {
  next: () => void;
  formStepOne: FormInstance<SamanConfiguration>;
  formStepTwo: FormInstance<SamanRowType>;
  requestTitle: string;
  setLoadingDelete: React.Dispatch<React.SetStateAction<boolean>>;
  setLoadingStepTwo: React.Dispatch<React.SetStateAction<boolean>>;
}

const SecondStepSaman: React.FC<Props> = ({
  formStepOne,
  formStepTwo,
  setLoadingDelete,
  next,
  setLoadingStepTwo,
  requestTitle,
}) => {
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const { breakpoints } = usePaypingContext();
  const { lg } = breakpoints;
  const paymentDetailsModels = useAppSelector((state) => state.digitalAccount.saman.requestList?.paymentDetailsModels);
  const [loadingUpload, setFileUploaded] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [modal, contextHolder] = Modal.useModal();
  const [modalVisible, setModalVisible] = useState(false);

  const requestError = paymentDetailsModels?.map((item) => item.errors).filter((errors) => errors);

  const [{ loading: loadingDelete }, handleDelete] = useMessagedAsyncFn(
    async (goNext?: boolean) => {
      setLoadingDelete(true);
      modal.confirm({
        cancelText: t("cancel"),
        content: t("message.remove", { subject: t("common.transfer") }),
        mask: { closable: true },
        okText: t("ok"),
        onOk: async () => {
          const updatedPaymentDetails = paymentDetailsModels?.filter((item) => !item.errors?.length);

          await dispatch(
            setSamanUpdateRequestList({
              paymentDetailsModels: updatedPaymentDetails?.map((item) => ({
                iban: item.iban,
                description: item.description,
                amount: item.amount,
                reasonCode: item.reasonCode,
              })),
            }),
          );

          if (goNext) {
            next();
          }
        },
      });
      setLoadingDelete(false);
    },

    [dispatch, modal, next, paymentDetailsModels, setLoadingDelete, t],
    undefined,
    { useProgress: true },
  );

  const [, requestFunction] = useMessagedAsyncFn(
    async (file) => {
      try {
        setFileUploaded(true);
        const Uploads = await dispatch(setSamanUploadList(file));
        return JSON.stringify(Uploads);
      } catch (error: any) {
        if (error.status >= 500) {
          setErrorText(t("settlement.error.serverError"));
          setModalVisible(true);
        } else {
          const contentType = error.headers.get("content-type");
          const errorMsg = contentType.includes("application/json") ? await error.json() : "";
          setErrorText(errorMsg.error);
          setModalVisible(true);
        }
        return error;
      }
    },
    [dispatch, t],
    undefined,
    {
      useProgress: true,
    },
  );

  const onChange = async (info: UploadChangeParam) => {
    await formStepOne.validateFields();
    const { status } = info.file;
    if (status === "uploading") {
      setFileUploaded(true);
    } else if (status === "done") {
      setFileUploaded(false);
    } else setFileUploaded(false);
  };

  const callApi = async (options: any) => {
    await formStepOne.validateFields();
    const { file, onSuccess, onError } = options;
    requestFunction(file)
      .then(async (result) => {
        onSuccess?.(JSON.parse(result));
      })
      .catch(onError);
    return {
      abort() {},
    };
  };

  const [{ loading }, onFinish] = useMessagedAsyncFn(
    async () => {
      setLoadingStepTwo(true);
      if (paymentDetailsModels) {
        if (!requestError?.length) {
          next();
        } else await handleDelete(true);
      }
      setLoadingStepTwo(false);
    },
    [paymentDetailsModels, next, requestError, handleDelete, setLoadingStepTwo],
    undefined,
    {
      useProgress: true,
    },
  );

  useMessagedAsync(
    async () => {
      formStepTwo.setFieldValue?.(
        "transfer",
        paymentDetailsModels?.map((item, index) => ({ ...item, id: index })),
      );
    },
    [formStepTwo, paymentDetailsModels],
    {
      success: false,
      useProgress: true,
    },
  );

  return (
    <LegacyForm<SamanRowType>
      name={"SamanRowType"}
      onFinish={onFinish}
      form={formStepTwo}
      className="tw:w-full"
      layout={"vertical"}
    >
      <Row gutter={[0, 16]} className={"tw:w-full"}>
        {lg ? null : (
          <LegacyForm.Item noStyle>
            <Col xs={24}>
              <Flex gap={"small"}>
                <Typography.Text>{requestTitle}</Typography.Text>
              </Flex>
            </Col>
          </LegacyForm.Item>
        )}
        <Modal footer={null} open={modalVisible} width={700} centered onCancel={() => setModalVisible(false)}>
          <div className="tw:px-1! tw:pt-2! tw:text-center">
            <FailedDollar width={100} height={100} className="tw:mx-auto tw:mb-2! tw:block" />
            <Typography.Text strong className="tw:my-3! tw:block tw:text-sm">
              {errorText}
            </Typography.Text>
            <Row justify="space-around" className="tw:mt-3!">
              <Button onClick={() => setModalVisible(false)} className="tw:w-full" size="large" type={"primary"}>
                {t("tryAgain")}
              </Button>
            </Row>
          </div>
        </Modal>
        <Col xs={24}>
          <div className="tw:flex tw:justify-end">
            <Upload
              // TODO: hidden until tested completely
              className="tw:hidden hidden"
              disabled={loading}
              key={1}
              onChange={onChange}
              customRequest={callApi}
              allowedExtensions={["xls", "xlsx"]}
              showUploadList={true}
              maxCount={1}
              data-google-track={fileUploadId}
              crop={false}
            >
              <Button loading={loadingUpload} icon={<UploadOutlined width={14} height={14} />}>
                {t("settlement.create.uploadFile")}
              </Button>
            </Upload>
            {lg ? (
              <Button
                className="tw:flex! tw:justify-end!"
                key={2}
                disabled={!(requestError?.length || loadingDelete)}
                onClick={() => handleDelete()}
                data-google-track={deleteErrorsId}
              >
                {t("deleteAllErrors")}
              </Button>
            ) : null}
          </div>
        </Col>
        <LegacyForm.List name={"transfer"}>
          {(fields) => (
            <>
              <Col xs={24}>{<TransferList fields={fields} form={formStepTwo} />}</Col>
            </>
          )}
        </LegacyForm.List>
        <Col xs={24}>
          <ManualRequestSaman form={formStepTwo} />
        </Col>
        {contextHolder}
      </Row>
    </LegacyForm>
  );
};

export default SecondStepSaman;
